import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { movies } from "@/mocks/movies";

const featuredIds = ["peach-2024", "peach-1993", "peach-2025"];
const slides = featuredIds
  .map((id) => movies.find((movie) => movie.id === id))
  .filter((movie): movie is (typeof movies)[number] => Boolean(movie));

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, []);

  const go = (next: number) => {
    setIndex((next + slides.length) % slides.length);
  };

  return (
    <section className="relative w-full overflow-hidden bg-foreground-950">
      <div className="relative h-[600px] w-full md:h-[680px]">
        {slides.map((movie, slideIndex) => (
          <div
            key={movie.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              slideIndex === index ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
            aria-hidden={slideIndex !== index}
          >
            <img
              src={movie.backdrop}
              alt={`${movie.title}剧照`}
              title={`${movie.title}剧情介绍与演员资料`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/60 to-foreground-950/20" />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-transparent to-foreground-950/40" />
          </div>
        ))}

        <div className="relative mx-auto flex h-full w-full max-w-[1240px] flex-col justify-end px-4 pb-20 pt-20 md:px-6 md:pb-28 md:pt-24">
          <div key={slides[index].id} className="max-w-2xl animate-fade-up">
            <p className="mb-4 flex flex-wrap items-center gap-2 text-[11px] tracking-[0.2em] text-background-100/80">
              <span className="rounded-full border border-primary-300/60 bg-primary-500/20 px-2.5 py-1 text-primary-100">
                系列焦点
              </span>
              <span className="uppercase">Peach Cinema Archive</span>
            </p>
            <h1 className="font-heading text-4xl font-bold leading-tight text-background-50 md:text-6xl">
              {slides[index].title}
            </h1>
            <p className="mt-2 text-xs uppercase tracking-[0.24em] text-background-200/70">
              {slides[index].englishTitle} · {slides[index].year}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-background-100/85">
              <span className="flex items-center gap-1.5">
                <i className="ri-star-fill text-primary-300" />
                {slides[index].rating.toFixed(1)} 分
              </span>
              <span className="h-3 w-px bg-background-50/25" />
              <span>{slides[index].genres.join(" / ")}</span>
              <span className="h-3 w-px bg-background-50/25" />
              <span>{slides[index].duration} 分钟</span>
              <span className="h-3 w-px bg-background-50/25" />
              <span>{slides[index].region}</span>
            </div>
            <p className="mt-5 line-clamp-3 max-w-xl text-sm leading-relaxed text-background-100/90 md:line-clamp-none md:text-base">
              {slides[index].summary}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${slides[index].id}`}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                查看剧情详情
                <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to="/category"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/35 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
              >
                浏览全部影片
              </Link>
            </div>
          </div>

          <div className="mt-10 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              {slides.map((movie, dotIndex) => (
                <button
                  key={movie.id}
                  type="button"
                  onClick={() => go(dotIndex)}
                  aria-label={`切换到${movie.title}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    dotIndex === index ? "w-10 bg-primary-400" : "w-5 bg-background-50/35 hover:bg-background-50/60"
                  }`}
                />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => go(index - 1)}
                aria-label="上一张"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-background-50/30 text-background-50 transition-colors hover:bg-background-50/15"
              >
                <i className="ri-arrow-left-line" />
              </button>
              <button
                type="button"
                onClick={() => go(index + 1)}
                aria-label="下一张"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-background-50/30 text-background-50 transition-colors hover:bg-background-50/15"
              >
                <i className="ri-arrow-right-line" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}